"use client";

import { createContext, useContext, type ReactNode } from "react";
import type { CurrencyCode } from "@/lib/api/permalinks";
import { formatPrice as formatPriceWithCurrency } from "@/lib/price";

interface CurrencyContextValue {
  currency: CurrencyCode;
  formatPrice: (amount: number | string) => string;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

export function CurrencyProvider({
  currency,
  children,
}: {
  currency: CurrencyCode;
  children: ReactNode;
}) {
  const value: CurrencyContextValue = {
    currency,
    formatPrice: (amount) => formatPriceWithCurrency(amount, currency),
  };
  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

/**
 * Reads the storefront currency configured by the NPharmacy plugin
 * (Settings → Storefront currency). Falls back to CAD when called outside
 * the provider — should not happen in practice, but keeps tests + dev
 * islands working without crashing.
 */
export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (ctx) return ctx;
  return {
    currency: "CAD",
    formatPrice: (amount) => formatPriceWithCurrency(amount, "CAD"),
  };
}
